import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  PLATFORM_ID,
  Signal,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';

import {
  asyncScheduler,
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  observeOn,
  of,
  skip,
  startWith,
  Subject,
  Subscription,
  take,
  withLatestFrom,
} from 'rxjs';
import { select, Store } from '@ngrx/store';

import { getViewedNotifications } from '@fcom/common/store/selectors/notification.selectors';
import { LocationPair } from '@fcom/common/store/store.interface';
import * as NotificationActions from '@fcom/common/store/actions/notification.actions';
import { AppPreloadingStrategyService } from '@fcom/core/services/preloading-module/appPreloadingStrategy.service';
import { AppState } from '@fcom/core/interfaces/app-state.interface';
import { compareAsJson, isEmptyObjectOrHasEmptyValues, isNotEmpty, isPresent } from '@fcom/core/utils/utils';
import { RootPaths } from '@fcom/core/constants';
import { unsubscribe } from '@fcom/core/utils/unsubscribe';
import { WindowRef } from '@fcom/core/providers/window';
import { ButtonMode, ButtonSize, ButtonTheme } from '@fcom/ui-components/components/buttons/button/enums';
import { NotificationTheme } from '@fcom/ui-components/components/notifications/enums';
import { PopoverService } from '@fcom/ui-components/components/popover/service/popover.service';
import { GlobalBookingTripDates } from '@fcom/common/interfaces/global-booking-trip-dates.interface';
import { MediaQueryService } from '@fcom/common/services/media-query/media-query.service';
import { PriceCalendarService } from '@fcom/common/services/price-calendar/price-calendar.service';
import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { Amount } from '@fcom/dapi/interfaces/dapi.interface';
import { LoginStatus } from '@fcom/core-api/login/interfaces/login.interface';
import { PriceCalendarType } from '@fcom/common/components/price-calendar/price-calendar.component';
import { locationPairIncludesTrainDestination } from '@fcom/common-booking/utils/common-booking.utils';
import { finShare } from '@fcom/rx/operators/finShare';
import { LanguageService } from '@fcom/ui-translate/services/language.service';

import { BOOKING_WIDGET_CONTEXT } from '../../constants';
import {
  ExpanderStatus,
  LocationType,
  SelectorType,
  WidgetLayout,
  WidgetTheme,
} from '../../interfaces/booking-widget.interface';
import { LocationParam } from '../../interfaces/location-pair.interface';
import { NotificationWarning } from '../../interfaces/notification-data.interface';
import { PaxUpdateEvent } from '../../interfaces/paxAmount.interface';
import { WidgetTab } from '../../interfaces/flight-search.interface';
import { BookingWidgetService } from '../../services/booking-widget.service';
import { BookingWidgetFlightService } from '../../services/booking-widget-flight.service';
import { BookingWidgetTripService } from '../../services/booking-widget-trip.service';
import { TravelDatesSelectorComponent } from '../../components/travel-dates-selector/travel-dates-selector.component';
import { seasonalNotificationHandler } from '../../utils/utils';
import * as BookingWidgetActions from '../../store/actions/booking-widget.actions';

@Component({
  selector: 'fin-booking-widget-common',
  templateUrl: './booking-widget-common.component.html',
  styleUrls: ['./booking-widget-common.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BookingWidgetCommonComponent implements OnInit, OnDestroy, AfterViewInit {
  private bookingWidgetService = inject(BookingWidgetService);
  private bookingWidgetFlightService = inject(BookingWidgetFlightService);
  private bookingWidgetTripService = inject(BookingWidgetTripService);
  private windowRef = inject(WindowRef);
  private store$ = inject<Store<AppState>>(Store);
  private preloadStrategyService = inject(AppPreloadingStrategyService);
  private priceCalendarService = inject(PriceCalendarService);
  private mediaQueryService = inject(MediaQueryService);
  private popoverService = inject(PopoverService);
  private languageService = inject(LanguageService);
  private platform = inject(PLATFORM_ID);

  readonly ButtonTheme = ButtonTheme;
  readonly ButtonSize = ButtonSize;
  readonly ButtonMode = ButtonMode;
  readonly TripType = TripType;
  readonly WidgetTab = WidgetTab;
  readonly LoginStatus = LoginStatus;
  readonly WidgetLayout = WidgetLayout;
  readonly WidgetTheme = WidgetTheme;
  readonly SelectorType = SelectorType;
  readonly LocationType = LocationType;
  readonly NotificationTheme = NotificationTheme;
  readonly NEW_MULTI_CITY_NOTIFICATION_ID = 87731561;
  readonly PriceCalendarType = PriceCalendarType;

  readonly compactMode = input(false);

  readonly defaultLocations = input<LocationPair[]>([]);

  readonly layout = input<WidgetLayout>(WidgetLayout.DEFAULT);

  readonly isGlobalBookingWidget = input(false);

  readonly usePopoverSelectors = input(false);

  readonly identifier = input<string>(undefined);

  readonly isFrontPage = input(false);

  readonly enablePromoCode = input(true);

  readonly disableOnDSelection = input(false);

  readonly changeGlobalBookingWidgetExpanderStatus = output<ExpanderStatus>();

  readonly footer: Signal<ElementRef<HTMLElement> | undefined> = viewChild('footer', { read: ElementRef });

  readonly travelDatesSelector = viewChild(TravelDatesSelectorComponent);

  readonly tripType = this.bookingWidgetTripService.selectedTripType;
  readonly travelClass = this.bookingWidgetTripService.selectedTravelClass;
  readonly paxAmount = this.bookingWidgetTripService.paxAmount;
  readonly locations = this.bookingWidgetFlightService.locations;
  readonly flights = this.bookingWidgetFlightService.flights;
  readonly travelDates = this.bookingWidgetService.travelDates;
  readonly bookingFlowType = this.bookingWidgetTripService.activeTab;
  readonly continueEnabled = this.bookingWidgetService.continueEnabled;
  readonly airCalendarPrices = this.bookingWidgetService.airCalendarPrices;
  readonly profile = this.bookingWidgetService.profile;
  private readonly isTabletDown = this.mediaQueryService.isBreakpoint('tablet_down');

  // Observable bridges kept for the RxJS pipelines below (combineLatest/withLatestFrom/skip/take);
  // templates read the signals above directly.
  private readonly tripType$ = toObservable(this.tripType);
  private readonly travelClass$ = toObservable(this.travelClass);
  private readonly paxAmount$ = toObservable(this.paxAmount);
  private readonly locations$ = toObservable(this.locations);
  private readonly flights$ = toObservable(this.flights);
  private readonly travelDates$ = toObservable(this.travelDates);
  private readonly bookingFlowType$ = toObservable(this.bookingFlowType);
  private readonly _currentSearch$ = toObservable(this.bookingWidgetService.currentSearch);
  private readonly _flightSearchParams$ = toObservable(this.bookingWidgetService.flightSearchParams);
  private readonly _isMultiCity$ = toObservable(this.bookingWidgetService.isMultiCity);
  private readonly _showCompact$ = toObservable(this.bookingWidgetService.showCompact);
  private readonly _notificationWarning$ = toObservable(this.bookingWidgetService.notificationWarning);
  private readonly _bookingSelections$ = toObservable(this.bookingWidgetService.bookingSelections);

  startingPrice$: Observable<Amount> = of(null);
  loading$: Subject<boolean> = new Subject<boolean>();
  isNewSearchEnabled$ = of(false);
  showCompact$ = of(true);
  subscription = new Subscription();
  showDiscountCode$: Observable<boolean>;
  shouldFocusWidgetContent$: Subject<void> = new Subject<void>();
  isNewMultiCityNotificationVisible$: Observable<boolean>;
  /**
   * @description When parameters changed and are valid you can enable the search again
   */
  shouldSearchBeEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showFlexibleDatesSelection$: Observable<boolean>;
  hideFooterPrice$: Observable<boolean>;
  showTripPriceFrom$: Observable<boolean>;
  private newSearch$: Subject<boolean> = new Subject<boolean>();

  constructor() {
    effect(() => this.bookingWidgetFlightService.setDefaultLocations(this.defaultLocations()));
  }

  ngOnInit(): void {
    this.subscription.add(
      combineLatest([this._flightSearchParams$, this._currentSearch$]).subscribe(([currentParams, lastSearch]) => {
        const areSameParams = compareAsJson(currentParams, lastSearch);
        if (!areSameParams) {
          const isValid = this.bookingWidgetService.validateFlightSearchParams(currentParams);
          this.shouldSearchBeEnabled$.next(isValid);
        } else {
          this.shouldSearchBeEnabled$.next(false);
        }
      })
    );
    this.bookingWidgetService.setCurrentSearchToCurrentParams();
    this.bookingWidgetService.setUsePopoverSelectors(this.usePopoverSelectors() && this.isGlobalBookingWidget());

    // Selections
    this.showCompact$ = this._showCompact$.pipe(map((showCompact) => showCompact && this.compactMode()));

    // Prices
    this.startingPrice$ = this.priceCalendarService.startingFromPrice$;

    //reset the change status of booking widget
    this.bookingWidgetService.resetLocalOriginalBookingSelection();

    this.showDiscountCode$ = combineLatest([this.bookingFlowType$, this.tripType$]).pipe(
      map(([bookingFlowType, tripType]) => {
        if (!this.enablePromoCode()) {
          return false;
        }

        if (bookingFlowType !== WidgetTab.FLIGHT) {
          return false;
        }

        return tripType !== TripType.MULTICITY;
      })
    );

    if (this.isGlobalBookingWidget()) {
      this.subscription.add(
        this._notificationWarning$
          .pipe(
            map((notification) =>
              Object.keys(notification)
                .filter((notificationType) => notificationType !== 'seasonalRoute')
                .reduce((filteredNotification, key) => {
                  filteredNotification[key] = notification[key];
                  return filteredNotification;
                }, {})
            ),
            filter((notification) => !isEmptyObjectOrHasEmptyValues(notification))
          )
          .subscribe(() => {
            this.changeGlobalBookingWidgetExpanderStatus.emit(ExpanderStatus.OPEN);
          })
      );
    }

    this.isNewMultiCityNotificationVisible$ = this.store$.pipe(
      select(getViewedNotifications),
      map((viewedNotifications) => {
        return !viewedNotifications.includes(this.NEW_MULTI_CITY_NOTIFICATION_ID);
      })
    );

    this.subscription.add(
      // on client interaction with the widget (2nd emission of booking selections), trigger preloading booking module
      this._bookingSelections$
        .pipe(skip(1), take(1))
        .subscribe(() => this.preloadStrategyService.triggerPreload(RootPaths.BOOKING_ROOT))
    );

    this.showFlexibleDatesSelection$ = combineLatest([this.bookingFlowType$, this.locations$]).pipe(
      map(([bookingFlowType, locations]) => {
        if (
          this.layout() === WidgetLayout.HORIZONTAL ||
          bookingFlowType === WidgetTab.AWARD ||
          locationPairIncludesTrainDestination(locations)
        ) {
          this.bookingWidgetService.setFlexibleDates(false);
          return false;
        }

        return true;
      }),
      finShare()
    );

    const noPrices$ = combineLatest([
      this.priceCalendarService.pricesAvailable$,
      this.priceCalendarService.pricesAvailableForSelectedDates$,
    ]).pipe(
      map(([pricesAvailable, pricesAvailableForSelectedDates]) => !pricesAvailable || !pricesAvailableForSelectedDates),
      startWith(false)
    );

    this.hideFooterPrice$ = combineLatest([this.bookingFlowType$, this.locations$, this.tripType$, noPrices$]).pipe(
      map(([bookingFlowType, locations, tripType, noPrices]) => {
        const isHorizontalLayout = this.layout() === WidgetLayout.HORIZONTAL;
        const isMulticity = tripType === TripType.MULTICITY;
        const isAwardFlow = bookingFlowType === WidgetTab.AWARD;
        const isTrainDestination = locationPairIncludesTrainDestination(locations);
        return isMulticity || isAwardFlow || isTrainDestination || isHorizontalLayout || noPrices;
      }),
      finShare()
    );

    this.showTripPriceFrom$ = this.locations$.pipe(
      map((locations) => {
        return !locationPairIncludesTrainDestination(locations);
      }),
      finShare()
    );

    this.subscription.add(
      combineLatest([
        this.tripType$,
        this.travelClass$,
        this.locations$,
        this.travelDates$,
        this.paxAmount$,
        this.languageService.lang,
      ])
        .pipe(
          filter(([tripType]) => tripType !== TripType.MULTICITY),
          map(([tripType, travelClass, { origin, destination }, travelDates, paxAmount, locale]) => ({
            origin: origin?.locationCode,
            destination: destination?.locationCode,
            tripType,
            travelClass,
            initialTravelDates: travelDates,
            paxAmount,
            locale,
          })),
          distinctUntilChanged(
            (prev, next) =>
              prev.origin === next.origin &&
              prev.destination === next.destination &&
              prev.tripType === next.tripType &&
              prev.travelClass === next.travelClass &&
              prev.initialTravelDates === next.initialTravelDates &&
              prev.locale === next.locale
          )
        )
        .subscribe((params) => this.priceCalendarService.updateParams(params))
    );

    this.subscription.add(
      this.newSearch$
        .pipe(
          filter(() => {
            return this.isTabletDown();
          })
        )
        .subscribe(() => {
          this.changeGlobalBookingWidgetExpanderStatus.emit(ExpanderStatus.CLOSE);
        })
    );

    this.subscription.add(
      this.priceCalendarService.availability$
        .pipe(
          filter((availability) => isNotEmpty(availability) && availability.length > 0),
          withLatestFrom(this.tripType$, this.languageService.translate<Record<string, string | string[]>>('date')),
          filter(([, tripType]) => tripType !== TripType.MULTICITY)
        )
        .subscribe(([availability, , dateTranslations]) => {
          const seasonalNotificationData = seasonalNotificationHandler(availability, dateTranslations);
          this.store$.dispatch(
            BookingWidgetActions.setNotificationWarning({
              key: NotificationWarning.SEASONAL_ROUTE,
              isActive: true,
              data: seasonalNotificationData,
            })
          );
        })
    );
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platform) && !this.isGlobalBookingWidget()) {
      this.subscription.add(
        this.shouldFocusWidgetContent$
          .pipe(
            withLatestFrom(this.showCompact$),
            filter(([, showCompact]) => !showCompact),
            observeOn(asyncScheduler),
            filter(() => this.footerIsNotInView())
          )
          .subscribe(() => {
            this.footer()?.nativeElement.scrollIntoView({
              block: 'end',
              inline: 'nearest',
              behavior: 'smooth',
            });
          })
      );
    }
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
    if (this.isGlobalBookingWidget()) {
      this.changeGlobalBookingWidgetExpanderStatus.emit(ExpanderStatus.CLOSE);
    }
  }

  setTripType(tripType: TripType): void {
    if (this.isGlobalBookingWidget()) {
      this.bookingWidgetService.setSelectionChangeType(SelectorType.TRIP_TYPE);
    }
    this.bookingWidgetTripService.setTripType(tripType);
  }

  openCalendar(): void {
    this.travelDatesSelector()?.openModal();
  }

  setPaxAmount({ paxType, amount, updateType }: PaxUpdateEvent): void {
    if (this.isGlobalBookingWidget()) {
      this.bookingWidgetService.setSelectionChangeType(SelectorType.PAX);
    }
    this.bookingWidgetTripService.setPaxAmount({ paxType, amount, updateType });
  }

  setLocations({ locations, locationType }: LocationParam, index = 0): void {
    if (this.isGlobalBookingWidget()) {
      locationType.forEach((type) => {
        this.bookingWidgetService.setSelectionChangeType(type as unknown as SelectorType);
      });
    }
    this.bookingWidgetFlightService.setLocations(locations, index);
    this.shouldFocusWidgetContent$.next();
  }

  setTravelDates(dates: GlobalBookingTripDates, isAirCalendar = false): void {
    if (this.isGlobalBookingWidget()) {
      this.bookingWidgetService.setSelectionChangeType(SelectorType.TRAVEL_DATES);
    }
    this.bookingWidgetService.setTravelDates(dates, 0, isAirCalendar, this.isGlobalBookingWidget());
  }

  setFlexibleDates(isFlexibleDates: boolean): void {
    this.bookingWidgetService.setFlexibleDates(isFlexibleDates);
  }

  onAddReturnClick(): void {
    if (this.isGlobalBookingWidget()) {
      this.bookingWidgetService.setSelectionChangeType(SelectorType.TRIP_TYPE);
    }
    this.bookingWidgetTripService.setReturnTripType();
  }

  cancelTravelDatesSelection(): void {
    const originalSelection = this.bookingWidgetService.currentSearch();
    this.bookingWidgetService.setTravelDates(
      {
        departureDate: originalSelection.flights[0]?.departureDate,
        returnDate: originalSelection.flights[1]?.departureDate,
      },
      0
    );

    this.bookingWidgetService.setTripType(originalSelection.tripType);
    this.bookingWidgetService.resetSearchSelection();
  }

  startNewSearch(): void {
    if (this.isGlobalBookingWidget()) {
      this.shouldSearchBeEnabled$.next(false);
    }
    this.bookingWidgetService.startNewSearch(this.isGlobalBookingWidget());
    this.popoverService.closeByContext(BOOKING_WIDGET_CONTEXT);
    this.newSearch$.next(true);
  }

  navigateToBookingFlow(fromMatrix = false): void {
    this.subscription.add(
      combineLatest([this._isMultiCity$, this.flights$, this.tripType$])
        .pipe(take(1))
        .subscribe(([isMultiCity, flights, tripType]) => {
          if (isMultiCity) {
            this.bookingWidgetService.navigateToMultiCityBookingFlow(this.loading$);
            return;
          }
          //check if there are only two flights and the 1st origin as same as 2nd destination, then set triptype to return
          if (
            tripType !== TripType.RETURN &&
            flights.length === 2 &&
            flights[0].origin.locationCode === flights[1].destination.locationCode &&
            flights[0].destination.locationCode === flights[1].origin.locationCode
          ) {
            this.bookingWidgetTripService.setTripType(TripType.RETURN);
            this.bookingWidgetService.setTravelDates(
              { departureDate: flights[0].departureDate, returnDate: flights[1].departureDate },
              0
            );
          }
          this.bookingWidgetService.navigateToBookingFlow(fromMatrix, this.loading$);
        })
    );
  }

  startSearch(): void {
    this.isGlobalBookingWidget() ? this.startNewSearch() : this.navigateToBookingFlow();
  }

  private footerIsNotInView(): boolean {
    const footer = this.footer();
    if (!isPresent(footer?.nativeElement)) {
      return false;
    }

    const footerRect = footer.nativeElement.getBoundingClientRect();
    const screenBottom = this.windowRef.nativeWindow.innerHeight;
    return footerRect.y + footerRect.height > screenBottom;
  }

  hideNewMultiCityNotification(): void {
    this.store$.dispatch(
      NotificationActions.setViewedNotification({
        id: this.NEW_MULTI_CITY_NOTIFICATION_ID,
      })
    );
  }
}
